'use client'

import React, { createContext, useContext, useState, useRef, useEffect } from 'react'

export type GlobalPopup = {
    id: number
    type: 'success' | 'error'
    message: string
}

type GlobalPopupContextType = {
    showPopup: (popup: Omit<GlobalPopup, 'id'>) => void
}

const GlobalPopupContext = createContext<GlobalPopupContextType | undefined>(undefined)

export const useGlobalPopup = () => {
    const context = useContext(GlobalPopupContext)
    if (!context) {
        throw new Error('useGlobalPopup must be used within a GlobalPopupProvider')
    }
    return context
}

export const GlobalPopupProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [popup, setPopup] = useState<GlobalPopup | null>(null)
    const [popupVisible, setPopupVisible] = useState(false)
    const [popupTransitionDuration, setPopupTransitionDuration] = useState(0.25)
    const popupIdRef = useRef(0)
    const timerRef = useRef<NodeJS.Timeout | null>(null)

    const showPopupInternal = (popup: GlobalPopup) => {
        setPopupTransitionDuration(0.25)
        setPopup(popup)
        setPopupVisible(true)
        if (timerRef.current) clearTimeout(timerRef.current)
        timerRef.current = setTimeout(() => {
            setPopupTransitionDuration(0.25)
            setPopupVisible(false)
            timerRef.current = null
        }, 3000)
    }

    const forceHidePopup = () => {
        setPopupTransitionDuration(0)
        setPopupVisible(false)
        if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }
    }

    const showPopup = (popup: Omit<GlobalPopup, 'id'>) => {
        const popupId = ++popupIdRef.current
        const popupWithId: GlobalPopup = { ...popup, id: popupId }
        if (popupVisible) {
            forceHidePopup()
            // Wait for instant exit before showing new popup
            setTimeout(() => showPopupInternal(popupWithId), 0)
        } else {
            showPopupInternal(popupWithId)
        }
    }

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current)
        }
    }, [])

    return (
        <GlobalPopupContext.Provider value={{ showPopup }}>
            {children}
            <GlobalPopupDisplay
                popup={popup}
                popupVisible={popupVisible}
                popupTransitionDuration={popupTransitionDuration}
                onAnimationComplete={() => {
                    if (!popupVisible) setPopup(null)
                }}
            />
        </GlobalPopupContext.Provider>
    )
}

// Separate component for the popup display
import { motion } from 'framer-motion'

type GlobalPopupDisplayProps = {
    popup: GlobalPopup | null
    popupVisible: boolean
    popupTransitionDuration: number
    onAnimationComplete: () => void
}

const GlobalPopupDisplay: React.FC<GlobalPopupDisplayProps> = ({
    popup,
    popupVisible,
    popupTransitionDuration,
    onAnimationComplete
}) => {
    if (!popup) return null

    return (
        <div className="fixed top-10 left-1/2 transform -translate-x-1/2 z-[9999] pointer-events-none">
            <motion.div
                key={popup.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: popupVisible ? 1 : 0 }}
                transition={{ duration: popupTransitionDuration }}
                onAnimationComplete={onAnimationComplete}
            >
                <div
                    className={`text-2xl text-white text-shadow-border ${popup.type === 'success'
                        ? 'shadow-green-800'
                        : 'shadow-red-800'
                        }`}
                >
                    {popup.message}
                </div>
            </motion.div>
        </div>
    )
}